import { useState, type CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { LoadingButton } from '@/components/loading-button'
import { cn } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import type { QuoteLine } from '../api/types'
import { AiData } from '../ui/ai-data'
import { PageState } from '../ui/page'
import { EditorModalContent } from '../editor/modal'
import { ManufacturerLink, ProductLink } from './links'
import { ProductImage } from './product-image'
import {
  MAX_DRAFT_ALTERNATIVES,
  equipmentAlternativesQueryOptions,
  type ComparisonEquipment,
  type EquipmentAlternatives,
} from './queries'

const savingsLabels: Record<ComparisonEquipment['savings'], string> = {
  current: 'Current equipment',
  lower: 'Savings opportunity',
  similar: 'Similar cost',
  higher: 'Higher cost',
  unknown: 'Not compared',
}
const tierLabels = { good: 'Good', better: 'Better', best: 'Best' } as const

function comparisonTitle(product: ComparisonEquipment) {
  let title = product.title
  for (const prefix of [product.manufacturer, product.model]) {
    if (title.toLowerCase().startsWith(`${prefix.toLowerCase()} `)) title = title.slice(prefix.length).trim()
  }
  return title === product.model ? null : title
}

// Comparisons never show financial fields, even if one arrives as a specification.
const FINANCIAL = /price|cost|margin|markup|discount|rebate|profit|savings|source|metadata/i
const CURRENCY = /[$£€]|\b(?:USD|EUR|GBP)\b/i

function comparisonFacts(product: ComparisonEquipment) {
  return product.specifications.flatMap((spec) => {
    if (FINANCIAL.test(`${spec.key} ${spec.label}`) || CURRENCY.test(spec.value)) return []
    const label = spec.label.trim()
    const value = spec.value.trim()
    const unit = spec.unit?.trim()
    if (!label || !value) return []
    return [
      {
        key: spec.key,
        label,
        value: unit && !value.toLowerCase().endsWith(unit.toLowerCase()) ? `${value} ${unit}` : value,
      },
    ]
  })
}

/**
 * The current equipment beside its same-category alternatives (demo `EquipmentComparison`):
 * relative tier and cost direction only, never prices; differing facts first, common facts folded.
 */
export function EquipmentComparison({
  comparison,
  selectedIds,
  onSelectionChange,
  disabled = false,
}: {
  comparison: EquipmentAlternatives
  selectedIds?: number[]
  onSelectionChange?: (ids: number[]) => void
  disabled?: boolean
}) {
  const products = [comparison.current, ...comparison.alternatives]
  const facts = products.map(comparisonFacts)
  const facets = [...new Map(facts.flat().map((fact) => [fact.key, fact.label])).entries()]
  const common = facets.filter(([key]) => {
    const values = facts.map((items) => items.find((fact) => fact.key === key)?.value)
    return values[0] !== undefined && values.every((value) => value === values[0])
  })
  const differences = facets.filter(([key]) => !common.some(([commonKey]) => commonKey === key))
  const selecting = Boolean(onSelectionChange)
  const selected = selectedIds ?? []

  return (
    <div className="min-w-0 text-sm" data-testid="equipment-comparison">
      {comparison.configured ? (
        <p className="mb-2 text-sm text-muted-foreground">Base equipment only. Options are not compared.</p>
      ) : null}
      <p className="mb-2 text-sm font-medium">Differences ({differences.length})</p>
      <div
        className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-[repeat(var(--comparison-columns),minmax(0,1fr))] md:gap-y-0"
        style={
          {
            '--comparison-columns': products.length,
            '--comparison-rows': differences.length + 3 + Number(selecting),
          } as CSSProperties
        }
      >
        {products.map((product, index) => (
          <section
            aria-label={`${index === 0 ? 'Current equipment' : 'Alternative'}: ${product.manufacturer} ${product.model}`}
            className={cn(
              'grid min-w-0 rounded-lg border bg-card md:grid-rows-subgrid md:[grid-row:span_var(--comparison-rows)]',
              selecting && selected.includes(product.product_id) && 'border-foreground/50 ring-1 ring-foreground/20',
            )}
            key={product.product_id}
          >
            <header className="min-w-0 p-3 [overflow-wrap:anywhere]">
              <ProductImage
                alt={`${product.manufacturer} ${product.model}`}
                className="h-24 w-full"
                url={product.primary_asset?.url}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                <ManufacturerLink uuid={product.manufacturer_uuid}>{product.manufacturer}</ManufacturerLink>
              </p>
              <h3 className="text-sm font-semibold">
                <ProductLink uuid={product.product_uuid}>{product.model}</ProductLink>
              </h3>
              <p className="mt-1 text-xs leading-4 text-muted-foreground">{comparisonTitle(product)}</p>
            </header>
            <AiData className="mx-3 my-2 px-2 py-1" label="AI-assisted relative tier">
              <dl>
                <dt className="text-xs text-muted-foreground" title="Based on list price, not a quality rating.">
                  Relative tier
                </dt>
                <dd className="mt-0.5 font-medium">{product.tier ? tierLabels[product.tier] : 'Not rated'}</dd>
              </dl>
            </AiData>
            <p
              className={cn(
                'border-t px-3 py-2 text-xs font-medium',
                product.savings === 'lower' ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground',
              )}
            >
              {index === 0 ? 'Current equipment' : savingsLabels[product.savings]}
            </p>
            {selecting ? (
              <div className="border-t px-3 py-1">
                {index === 0 ? (
                  <span className="flex min-h-9 items-center text-xs text-muted-foreground">On quote</span>
                ) : (
                  <label className="flex min-h-9 cursor-pointer items-center gap-2 text-sm font-medium">
                    <input
                      aria-label={`Show ${product.manufacturer} ${product.model} on draft`}
                      checked={selected.includes(product.product_id)}
                      className="size-4 shrink-0 accent-primary"
                      disabled={
                        disabled ||
                        (!selected.includes(product.product_id) && selected.length >= MAX_DRAFT_ALTERNATIVES)
                      }
                      onChange={(event) =>
                        onSelectionChange?.(
                          event.target.checked
                            ? [...selected, product.product_id]
                            : selected.filter((id) => id !== product.product_id),
                        )
                      }
                      type="checkbox"
                    />
                    Show on draft
                  </label>
                )}
              </div>
            ) : null}
            {differences.map(([key, label]) => (
              <AiData
                className="mx-3 my-1 px-2 py-1 last:mb-3 [overflow-wrap:anywhere]"
                key={key}
                label={`AI-assisted comparison: ${label}`}
              >
                <dl>
                  <dt className="text-xs text-muted-foreground">{label}</dt>
                  <dd className="mt-0.5 font-medium">
                    {facts[index]?.find((fact) => fact.key === key)?.value || 'Not specified'}
                  </dd>
                </dl>
              </AiData>
            ))}
          </section>
        ))}
      </div>
      {common.length ? (
        <details className="mt-3 border-t text-sm text-muted-foreground">
          <summary className="cursor-pointer py-3 font-medium">Common facts ({common.length})</summary>
          <dl className="grid gap-x-5 gap-y-2 pb-3 sm:grid-cols-2">
            {common.map(([key, label]) => (
              <div className="min-w-0 [overflow-wrap:anywhere]" key={key}>
                <dt>{label}</dt>
                <dd className="text-foreground">{facts[0]?.find((fact) => fact.key === key)?.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
      {!facets.length ? (
        <p className="mt-3 text-sm text-muted-foreground">No specifications available to compare.</p>
      ) : null}
    </div>
  )
}

/** Choose up to three alternatives to show on draft publications (demo `EquipmentAlternativesDialog`). */
export function EquipmentAlternativesDialog({
  quoteId,
  line,
  busy,
  error = false,
  onSave,
  onClose,
  returnFocus,
}: {
  quoteId: string
  line: QuoteLine
  busy: boolean
  error?: boolean
  onSave: (productIds: number[]) => void
  onClose: () => void
  returnFocus: () => void
}) {
  const api = useQuoteBuilderApi()
  const query = useQuery(equipmentAlternativesQueryOptions(api, quoteId, line))
  const initialSelectedIds = line.alternative_product_ids ?? []
  const [choices, setChoices] = useState<number[] | null>(null)
  const selected = choices ?? query.data?.selected_product_ids ?? initialSelectedIds
  const saved = query.data?.selected_product_ids ?? initialSelectedIds
  const changed = selected.length !== saved.length || selected.some((id) => !saved.includes(id))
  // Saved choices that no longer qualify can still be cleared when the comparison fails to load.
  const clearingUnavailable = choices?.length === 0 && initialSelectedIds.length > 0
  const model = line.model || ''

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open && !busy) onClose()
      }}
      open
    >
      <EditorModalContent
        closeDisabled={busy}
        description={query.data?.category || 'Equipment comparison'}
        footer={
          <>
            {error ? (
              <p className="mb-2 text-sm text-destructive" role="alert">
                Choices were not saved. Try Save choices again.
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-auto text-sm text-muted-foreground" role="status">
                {selected.length} of {MAX_DRAFT_ALTERNATIVES} selected
              </span>
              <Button disabled={busy || !selected.length} onClick={() => setChoices([])} variant="ghost">
                Clear all
              </Button>
              <LoadingButton
                disabled={(!query.data && !clearingUnavailable) || (!changed && !error)}
                loading={busy}
                loadingText="Saving choices..."
                onClick={() => onSave(selected)}
              >
                Save choices
              </LoadingButton>
            </div>
          </>
        }
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          returnFocus()
        }}
        size="xl"
        title={`Alternatives${model ? ` / ${model}` : ''}`}
      >
        {query.isPending ? (
          <PageState compact kind="loading" title="Loading alternatives" />
        ) : !query.data ? (
          <PageState
            action={
              <Button onClick={() => void query.refetch()} size="sm" variant="outline">
                Try again
              </Button>
            }
            compact
            kind="error"
            title="Alternatives could not be loaded"
          />
        ) : (
          <>
            {!query.data.alternatives.length ? (
              <p className="mb-4 text-sm text-muted-foreground">No comparable equipment found.</p>
            ) : null}
            <EquipmentComparison
              comparison={query.data}
              disabled={busy}
              onSelectionChange={setChoices}
              selectedIds={selected}
            />
          </>
        )}
      </EditorModalContent>
    </Dialog>
  )
}

/** Read-only same-category comparison under a product's details. */
export function ProductAlternatives({ quoteId, line }: { quoteId: string; line: QuoteLine }) {
  const api = useQuoteBuilderApi()
  const query = useQuery(equipmentAlternativesQueryOptions(api, quoteId, line))
  return (
    <section aria-label="Budget and preferred alternatives" className="mt-6 min-w-0 border-t pt-4">
      <h3 className="mb-3 text-sm font-semibold">Budget & preferred alternatives</h3>
      {query.isPending ? (
        <p className="text-sm text-muted-foreground" role="status">
          Loading alternatives...
        </p>
      ) : !query.data ? (
        <div className="space-y-2" role="alert">
          <p className="text-sm text-muted-foreground">Alternatives could not be loaded.</p>
          <Button onClick={() => void query.refetch()} size="sm" variant="outline">
            Try again
          </Button>
        </div>
      ) : query.data.alternatives.length ? (
        <EquipmentComparison comparison={query.data} />
      ) : (
        <p className="text-sm text-muted-foreground">No same-category alternatives available.</p>
      )}
    </section>
  )
}
